import { StyleSheet, type ViewStyle } from 'react-native';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';
import { GAME_LB_COLORS } from './GameLeaderboardSheet.constants';

/** Min sliver of dimmed hub left above the sheet when there's no notch (so it never looks full-screen). */
export const SHEET_TOP_GAP_MIN = 24;

export const gameLeaderboardSheetStyles = StyleSheet.create({
  // BaseModal content wrapper — full width, anchored to the bottom (contentAlignment="flex-end").
  modalContent: {
    width: '100%',
  },
  // Height is applied dynamically (screen - top safe inset) via sheetHeightStyle so it always fits
  // small screens and starts below the notch; bottom-anchored so it can never overflow above.
  sheet: {
    width: '100%',
    backgroundColor: GAME_LB_COLORS.sheetBg,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    borderTopWidth: 1,
    borderColor: GAME_LB_COLORS.sheetBorder,
    overflow: 'hidden',
  },
  handle: {
    width: 38,
    height: 4,
    borderRadius: 9999,
    backgroundColor: GAME_LB_COLORS.handle,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 12,
  },

  // Header
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: WARMUP_COLORS.text,
    letterSpacing: -0.2,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 9999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: GAME_LB_COLORS.closeBg,
  },

  // Tabs
  tabRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  tab: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 9999,
  },
  tabSelected: {
    backgroundColor: GAME_LB_COLORS.tabSelectedBg,
  },
  tabIdle: {
    backgroundColor: GAME_LB_COLORS.tabIdleBg,
  },
  tabTextSelected: {
    fontSize: 13.5,
    fontWeight: '700',
    color: GAME_LB_COLORS.tabSelectedText,
  },
  tabTextIdle: {
    fontSize: 13.5,
    fontWeight: '700',
    color: GAME_LB_COLORS.tabIdleText,
  },

  // Body + states
  body: {
    flex: 1,
    minHeight: 0,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  stateBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    padding: 24,
  },
  stateText: {
    fontSize: 14,
    fontWeight: '600',
    color: WARMUP_COLORS.textDim,
    textAlign: 'center',
  },
  retryButton: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 9999,
    backgroundColor: GAME_LB_COLORS.tabSelectedBg,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '800',
    color: GAME_LB_COLORS.tabSelectedText,
  },
  pressed: {
    opacity: 0.7,
  },

  // Podium
  podiumRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 18,
  },
  podiumCol: {
    flex: 1,
  },
  podiumPlace: {
    alignItems: 'center',
  },
  podiumCrown: {
    marginBottom: 2,
  },
  podiumAvatarWrap: {
    padding: 2,
    borderRadius: 9999,
  },
  podiumRingGold: {
    borderWidth: 3,
    borderColor: GAME_LB_COLORS.crown,
  },
  podiumRingMe: {
    borderWidth: 2,
    borderColor: WARMUP_COLORS.purple,
  },
  podiumRingNone: {
    borderWidth: 0,
  },
  podiumName: {
    fontSize: 13,
    fontWeight: '700',
    color: WARMUP_COLORS.text,
    marginTop: 6,
    maxWidth: 96,
  },
  podiumNameMe: {
    color: GAME_LB_COLORS.nameMeText,
  },
  podiumScore: {
    fontSize: 15,
    fontWeight: '800',
    color: GAME_LB_COLORS.score,
    marginTop: 1,
  },
  podiumPedestal: {
    marginTop: 8,
    width: '100%',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    alignItems: 'center',
    paddingTop: 6,
  },
  podiumPedestalRank: {
    fontSize: 20,
    fontWeight: '900',
    color: '#161021',
  },

  // List rows
  list: {
    gap: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: GAME_LB_COLORS.rowBg,
  },
  rowMe: {
    backgroundColor: GAME_LB_COLORS.rowMeBg,
  },
  rowRank: {
    width: 24,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '700',
    color: GAME_LB_COLORS.rankText,
  },
  rowName: {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    fontWeight: '600',
    color: GAME_LB_COLORS.nameText,
  },
  rowNameMe: {
    fontWeight: '800',
    color: GAME_LB_COLORS.nameMeText,
  },
  rowScore: {
    fontSize: 15,
    fontWeight: '800',
    color: GAME_LB_COLORS.score,
  },

  // Pinned "You" row
  youBar: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 14,
    backgroundColor: GAME_LB_COLORS.youBarBg,
    borderTopWidth: 1,
    borderColor: GAME_LB_COLORS.sheetBorder,
  },
  youInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: GAME_LB_COLORS.youInnerBg,
    borderWidth: 1,
    borderColor: GAME_LB_COLORS.youInnerBorder,
  },
  youRank: {
    width: 24,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '800',
    color: GAME_LB_COLORS.youRank,
  },
  youTextCol: {
    flex: 1,
    minWidth: 0,
  },
  youName: {
    fontSize: 14.5,
    fontWeight: '800',
    color: WARMUP_COLORS.text,
  },
  youContext: {
    fontSize: 11.5,
    fontWeight: '600',
    color: GAME_LB_COLORS.youContext,
  },
  youScore: {
    fontSize: 16,
    fontWeight: '800',
    color: GAME_LB_COLORS.score,
  },
});

/**
 * Runtime-dynamic styles (depend on window height + safe-area insets, so they can't live in
 * StyleSheet.create — mirrors the sanctioned `gameDynamicStyles.helper` pattern).
 */

/** Explicit sheet height = screen minus the top gap, so it always fits and starts below the notch. */
export function sheetHeightStyle(height: number): ViewStyle {
  return { height };
}

/** Pad the pinned "You" bar above the home indicator (bottom safe-area inset). */
export function youBarSafeAreaStyle(bottomInset: number): ViewStyle {
  return { paddingBottom: 14 + bottomInset };
}
